import React, { Dispatch, SetStateAction } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { format } from 'date-fns';

import { UseOutletContextSale } from '../Sale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Quote } from '../../../../models/quote.model';
import { Order } from '../../../../models/order.model';
import { Invoice } from '../../../../models/invoice.model';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { evaluateLegendRequired } from '../../../../scripts/validate.legend.script';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { contactTypeValue } from '../../../../libraries/contact.type.library';
import { formatContact } from '../../../../scripts/format.value.script';

import AppLegend from '../../../../components/element/Legend';
import AppInputDatePayment from '../../../../components/element/InputDatePayment';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';
import AppInputLanguage from '../../../../components/element/InputLanguage';
import AppInputQrType from '../../../../components/element/InputQrType';
import AppInputDicount from '../../../../components/element/InputDiscount';
import AppInputForeignCurrency, { ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';
import { DunningPolicy } from '../../../../models/settings.dunning-policies.model';

export interface AppSaleFormUpdateProps {
  sale: string,
  record: any | Quote | Order | Invoice,
  concerning: {value: string, valid: boolean},
  date: {value: string, valid: boolean},
  validUntil: {value: string, valid: boolean},
  deadline: {value: string, valid: boolean, object: any},
  currency: {value: string, valid: boolean, object: any},
  treasuryAccount: {value: string, valid: boolean, object: any},
  language: {value: string, valid: boolean},
  qrType?: {value: string, valid: boolean},
  discountType: {value: string, valid: boolean},
  discountValue: {value: string, valid: boolean},
  foreignCurrency?: ForeignCurrencyState,
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setValidUntil: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDeadline: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setLanguage: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrType?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDiscountType: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDiscountValue: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setForeignCurrency?: Dispatch<SetStateAction<ForeignCurrencyState>>,
  dunningPolicyId?: string,
  setDunningPolicyId?: Dispatch<SetStateAction<string>>,
  dunningPolicies?: DunningPolicy[]
};

const AppSaleFormUpdate: React.FunctionComponent<AppSaleFormUpdateProps> = ({sale, record, concerning, date, validUntil, deadline, currency, treasuryAccount, language, qrType, discountType, discountValue, foreignCurrency, setConcerning, setDate, setValidUntil, setDeadline, setCurrency, setTreasuryAccount, setLanguage, setQrType, setDiscountType, setDiscountValue, setForeignCurrency, dunningPolicyId, setDunningPolicyId, dunningPolicies}) => {
  const {companyForUser, settingsForCompany, treasuryAccountsForCompany, loadSettingsForCompany} = UseOutletContextSale()
  const {lang} = StateLanguage()

  const handleChangeConcerning = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const validateConcerning = () => {
    evaluateLegendRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  return (
    <>
      { companyForUser && settingsForCompany && treasuryAccountsForCompany &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h2>
                {sale === "quote" && record.quote_number}
                {sale === "order" && record.order_number}
                {sale === "invoice" && record.invoice_number}
              </h2>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="d-flex flex-wrap mb-5">
              <div className="flex-equal me-5">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    <tr>
                      <td className="w-200px text-muted">{lang.labels.company}:</td>
                      <td className="min-w-200px">{record.company.name}</td>
                    </tr>
                    <tr>
                      <td className="w-200px text-muted">{lang.labels.customer}:</td>
                      <td className="min-w-200px">
                        <div className="d-flex align-items-center">
                          {formatContact(record.customer)}
                          <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, record.customer?.type ?? '')}</span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                      <td className="min-w-200px">{datetimeFormat12h(lang.code, record.created_at)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex-equal">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    <tr>
                      <td className="w-200px text-muted">{lang.labels[`${sale}Reference` as keyof typeof lang.labels]}:</td>
                      <td className="min-w-200px">{(record.reference ?? '').length > 0 ? record.reference : lang.labels.none}</td>
                    </tr>
                    { sale !== "quote" &&
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.quoteNumber}:</td>
                        <td className="min-w-200px">{record.quote?.id?.length > 0 ? record.quote.quote_number : lang.labels.none}</td>
                      </tr>
                    }
                    { sale === "invoice" &&
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.orderNumber}:</td>
                        <td className="min-w-200px">{record.order?.id?.length > 0 ? record.order.order_number : lang.labels.none}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
            <div className="fw-bold fs-6 mb-5">{lang.labels[`pleaseModifyAddOrDeleteFieldsFrom${sale.charAt(0).toUpperCase()}${sale.slice(1)}` as keyof typeof lang.labels]}</div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.concerning}</label>
              <div className="fv-row">
                <TextareaAutosize className="form-control" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.creationDate}</label>
              <div className="fv-row">
                <input className="form-control" type="date" name="date" max={currentDate()} value={date.value.substring(0, 10)} onChange={handleChangeDate} />
                <AppLegend attribute={{check: date.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.expirationDate}</label>
              <div className="fv-row">
                <AppInputDatePayment companyForUser={companyForUser} settingsForCompany={settingsForCompany} validUntil={validUntil} deadline={deadline} setValidUntil={setValidUntil} setDeadline={setDeadline} loadSettingsForCompany={loadSettingsForCompany}></AppInputDatePayment>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.currency}</label>
              <div className="fv-row">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            { currency.value && companyForUser && currency.value !== companyForUser.currency?.code && foreignCurrency && setForeignCurrency &&
              <div className="row mb-3">
                <label className="form-label">{lang.labels.exchangeRate}</label>
                <div className="fv-row">
                  <AppInputForeignCurrency companyId={companyForUser.id} companyCurrencyCode={companyForUser.currency?.code} selectedCurrency={currency.object} foreignCurrency={foreignCurrency} setForeignCurrency={setForeignCurrency}></AppInputForeignCurrency>
                </div>
              </div>
            }
            { sale === 'invoice' &&
              <div className="row mb-3">
                <label className="form-label required">{lang.labels.treasuryAccount}</label>
                <div className="fv-row">
                  <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
                </div>
              </div>
            }
            <div className="row mb-0 mb-lg-2">
              <label className="form-label required">{lang.labels.language}</label>
              <div className="fv-row">
                <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
              </div>
            </div>
            { qrType && setQrType &&
              <div className="row mb-0 mb-lg-2">
                <label className="form-label required">{lang.labels.qrType}</label>
                <div className="fv-row">
                  <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
                </div>
              </div>
            }
            { sale === 'invoice' && dunningPolicies && setDunningPolicyId &&
              <div className="row mb-0">
                <label className="form-label">{lang.labels.dunningPolicy}</label>
                <div className="fv-row">
                  <select className="form-select" value={dunningPolicyId ?? ''} onChange={(e) => setDunningPolicyId(e.target.value)}>
                    <option value="">{lang.labels.noDunning}</option>
                    { dunningPolicies.map((policy, index) => (
                      <option key={index} value={policy.id}>{policy.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            }
            <div className="row d-none">
              <label className="form-label required">{lang.labels.discount}</label>
              <div className="fv-row">
                <AppInputDicount discountType={discountType} discountValue={discountValue} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue}></AppInputDicount>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppSaleFormUpdate;
